import './assets/main.css';
import { createApp, markRaw } from 'vue';
import { createPinia } from 'pinia';
import App from '@/App.vue';
import router from '@/router';
import type { FrontEnd } from '@shared/api';
import type { Emitter } from 'mitt';
import mitt from 'mitt';
import { MotionPlugin } from '@vueuse/motion';
import { Icon } from '@iconify/vue';
import type { Plugin, Component } from 'vue';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { vueQueryOptions } from '@infra/plugins/vue-query';
import VueLazyload from 'vue-lazyload';
import { createHead } from '@unhead/vue';
import { notivue } from '@infra/plugins/notivue';
import { useTenantStore } from '@features/tenant/store/tenant';
import TenantNotFound from '@features/tenant/components/TenantNotFound.vue';
import DOMPurify from 'dompurify';
import { startAwsRum } from '@infra/analytics/aws-rum-web.ts';
import {
  installAudioContextHooks,
  registerAudioVisibilityHandlers,
} from '@infra/audio/audio-context-hooks';
import { bootColorScheme } from '@features/tenant/colorScheme';
import { startColorScheme } from '@features/tenant/colorSchemeRuntime';

bootColorScheme(window);

startAwsRum();

const emitter: Emitter<FrontEnd> = mitt<FrontEnd>();

const app = createApp(App as Component);

app.config.globalProperties.emitter = emitter;
app.provide('emitter', emitter);

const pinia = createPinia();
pinia.use(({ store }) => {
  store.router = markRaw(router);
  store.emmiter = markRaw(emitter);
});

app.use(pinia);
app.use(router);
app.use(MotionPlugin as Plugin);
app.use(VueLazyload, {
  preLoad: 1.3,
  error: 'https://fakeimg.pl/736x411/3b3b3b/3b3b3b',
  loading: 'https://fakeimg.pl/736x411/3b3b3b/3b3b3b',
  attempt: 1,
});
app.use(notivue);
app.component('IconifyIcon', Icon);
app.provide('emitter', emitter);

app.directive('safe-html', (el: HTMLElement, binding) => {
  el.innerHTML = DOMPurify.sanitize(binding.value as string);
});

app.use(VueQueryPlugin, vueQueryOptions);

app.use(createHead());

installAudioContextHooks();
registerAudioVisibilityHandlers();

/**
 * The tenant is resolved before the app mounts, not after.
 *
 * Everything downstream depends on the answer: which brand, which login
 * providers, which operation every request is scoped to. Mounting first and
 * resolving later is how one tenant flashes another's colours on first paint.
 *
 * Only an unowned hostname stops the boot. A backend that is merely down still
 * mounts, degraded, because a blank page is worse than a working app with the
 * default look.
 */
async function bootstrap() {
  startColorScheme();
  const tenant = useTenantStore();
  await tenant.resolve();

  if (tenant.status === 'unknown-host') {
    createApp(TenantNotFound, { host: window.location.hostname }).mount('#app');
    return;
  }

  app.mount('#app');
}

void bootstrap();
// force actions
